import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SplashScreen } from './SplashScreen';
import ExpiredReadOnlyBanner from './ExpiredReadOnlyBanner';
import { apiGet } from '../utils/apiClient';
import { hasAcknowledgedTrialInterstitial } from './TrialExpiredInterstitial';
import { isSignupFinishing } from '../utils/signupFlow';

// Cached /user/me shared by every PrivateRoute within a session, so the account gate renders
// immediately from the cache and revalidates in the background, gating on the splash only when
// nothing is cached yet (true first load). Cleared on sign-out.
//
// Note react-router (v6) usually REUSES the same PrivateRoute instance across navigations (routes
// are not keyed), so the component's own `profile` state survives a route change too. That is
// why clearing needs a generation counter, not just `cachedProfile = null`: see below.
let cachedProfile = null;
let profileGeneration = 0;

// Call after an action that changes access (e.g. starting a trial). Every PrivateRoute treats the
// profile it holds as stale and re-fetches before applying any redirect; without this a reused
// instance would redirect on its old copy (e.g. bounce a new trial user back to the plan screen).
export function clearCachedProfile() {
  cachedProfile = null;
  profileGeneration += 1;
}

export function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hasUser = !!user;
  const userId = user?.uid || '';
  const [profileState, setProfileState] = useState({ value: cachedProfile, generation: profileGeneration });
  const [profileLoading, setProfileLoading] = useState(!cachedProfile);
  const profileIsStale = profileState.generation !== profileGeneration;
  const profile = profileIsStale ? null : profileState.value;
  // '/More' is here for a legal reason, not a product one. Someone whose household was removed
  // is otherwise pinned to the interstitial until they start a trial — with no route to Delete
  // Account or Export your data. A person must always be able to remove their own data and take
  // a copy of it, whether or not they choose to subscribe.
  const accessEndedAllowedPaths = ['/household-access-ended', '/household-access/start-trial', '/More'];
  // Screens that ONLY make sense while access has ended, so a normal user gets bounced off them.
  // '/More' is deliberately absent — it is a normal screen that the ended state also permits.
  const accessEndedOnlyPaths = ['/household-access-ended', '/household-access/start-trial'];
  // '/plans/subscribed' is where Stripe Checkout returns: someone subscribing from a read-only or
  // no-plan state arrives there before the new plan has reached this cached profile.
  const trialExpiredAllowedPaths = ['/trial-expired', '/plans', '/plans/subscribed'];
  // An owner with no plan and no trial (e.g. left sign-up part-way) is sent to the plan screen.
  // The onboarding screens are allowed so a fresh sign-up can finish; '/More' for the same legal
  // reason as above (delete account / export data); '/plans' to subscribe.
  const planChoiceAllowedPaths = ['/onboarding/plan', '/onboarding/start-day', '/onboarding/servings', '/More', '/plans', '/plans/subscribed'];

  const hasAccessEnded = profile?.householdAccessTransition?.status === 'ended';
  const isExpiredAccount = (profile?.subscriptionTier || '').toLowerCase() === 'expired';
  const needsTrialExpiredInterstitial =
    isExpiredAccount && !hasAcknowledgedTrialInterstitial(profile);
  const needsPlanChoice = profile?.needsPlanChoice === true;

  useEffect(() => {
    let cancelled = false;

    if (!hasUser) {
      cachedProfile = null;
      setProfileState({ value: null, generation: profileGeneration });
      setProfileLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // Only gate on the splash when we have nothing cached yet; otherwise revalidate silently.
    if (!cachedProfile) setProfileLoading(true);
    const generation = profileGeneration;
    (async () => {
      try {
        const p = await apiGet('/user/me');
        if (!cancelled) {
          cachedProfile = p;
          setProfileState({ value: p, generation });
        }
      } catch (err) {
        console.warn('Failed to load account access state:', err);
        // Keep the last-known profile on a transient error rather than dropping the gate.
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasUser, userId, location.pathname, profileIsStale]);

  if (loading || (user && (profileLoading || profileIsStale))) {
    return <SplashScreen onComplete={() => {}} />;
  }

  if (!user) {
    return <Navigate to="/auth/signin" replace />;
  }

  if (hasAccessEnded && !accessEndedAllowedPaths.includes(location.pathname)) {
    return <Navigate to="/household-access-ended" replace />;
  }

  if (needsTrialExpiredInterstitial && !trialExpiredAllowedPaths.includes(location.pathname)) {
    return <Navigate to="/trial-expired" replace state={{ from: location.pathname }} />;
  }

  if (!hasAccessEnded && accessEndedOnlyPaths.includes(location.pathname)) {
    return <Navigate to="/MealPlan" replace />;
  }

  if (needsPlanChoice && !planChoiceAllowedPaths.includes(location.pathname)) {
    return <Navigate to="/onboarding/plan" replace />;
  }

  if (!needsTrialExpiredInterstitial && location.pathname === '/trial-expired') {
    return <Navigate to="/MealPlan" replace />;
  }

  return (
    <>
      {/* Not on the plan screens: its only action is "View plans", which would go nowhere there,
          and Plan & billing explains the read-only state itself. */}
      {isExpiredAccount && !['/trial-expired', '/plans', '/plans/subscribed'].includes(location.pathname) && <ExpiredReadOnlyBanner />}
      {children}
    </>
  );
}

export function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <SplashScreen onComplete={() => {}} />;
  }

  // While a sign-up is finishing, stay on the sign-up page; it routes to onboarding itself.
  if (user && !isSignupFinishing()) {
    return <Navigate to="/MealPlan" replace />;
  }

  return children;
}
